import { useEffect, useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import LumetierLogo from "@/components/LumetierLogo";
import CoverReveal from "@/pages/Gallery/shared/CoverReveal";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, RotatedCaption, FilmGrain, Ticker,
} from "@/components/editorial";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * PremiumCover — Phase 3 editorial gallery title card.
 * Full-bleed cinematic photograph (first photo or `gallery.cover_image`) with
 * a vignette overlay, top brand mark + live ticker, lower title block with
 * gold-italic accent, rotated chapter caption on the left edge, and a pulsing
 * vertical scroll cue that drops into the grid below.
 *
 * Same external props as the legacy cover: { gallery, accent }.
 */
export default function PremiumCover({ gallery, accent, selectedCount: selectedProp = 0 }) {
  const photos = gallery?.photos || [];
  const firstPhoto = photos[0];

  // Choose the cover background. Prefer gallery.cover_image; otherwise use the
  // first photo's url. All gallery photo urls are /api/files/<id> served by the
  // backend; the cover_image may already be absolute. Route through the resize
  // ── Cover layout variants ──────────────────────────────────────────
  // Maps the admin "Cover layout" choice to (a) the image geometry and
  // (b) where the title block sits. Page-style layouts (novel, frame,
  // journal, divider) put the photo on a solid page whose color follows
  // the overlay tone so the ink var keeps its contrast guarantee.
  const L = ["center", "left", "novel", "vintage", "frame", "stripe", "divider", "journal", "cinematic"].includes(gallery?.cover_layout)
    ? gallery.cover_layout
    : "center";
  const IMG_BOX = {
    frame: "absolute inset-3 md:inset-8 border-[6px] md:border-[10px] border-[color:var(--gold-foil)]/35",
    novel: "absolute left-1/2 -translate-x-1/2 top-[7%] bottom-[30%] w-[78%] md:w-[44%]",
    journal: "absolute inset-y-0 left-0 w-full md:w-[62%]",
    divider: "absolute inset-x-0 top-0 bottom-[20%]",
  }[L] || "absolute inset-0";
  const isPage = ["frame", "novel", "journal", "divider"].includes(L);
  const CONTENT = {
    center: "items-center justify-center text-center",
    vintage: "items-end justify-center text-center pb-28 lg:pb-36",
    stripe: "items-center justify-center text-center",
    divider: "items-end justify-center text-center pb-4 lg:pb-8",
    novel: "items-end justify-center text-center pb-6 lg:pb-10",
    frame: "items-end justify-center text-center pb-16 lg:pb-24",
    journal: "items-center justify-end",
    cinematic: "items-end pb-12 lg:pb-16",
    left: "items-end pb-32 lg:pb-40",
  }[L];
  const centered = ["center", "vintage", "stripe", "divider", "novel", "frame"].includes(L);

  // proxy at 2000px — that bucket is pre-generated at upload time, so the
  // cover paints instantly instead of waiting on a cold on-demand 2400px resize.
  const rawCover = gallery?.cover_image || firstPhoto?.url || "";
  const coverW = gallery?.design_preview ? 1200 : 2000;
  const _appendResize = (u) => u + (u.includes("?") ? "&" : "?") + `w=${coverW}&q=88`;
  const bg = rawCover
    ? (rawCover.startsWith("http") ? rawCover : _appendResize(`${BACKEND}${rawCover}`))
    : null;

  // Preload the cover image for instant first-paint.
  useEffect(() => {
    if (!bg) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = bg;
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (_) {} };
  }, [bg]);

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 160]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.10]);

  // Selected count — Phase 6: prop from the orchestrator (live favCount).
  // Falls back to 0 cleanly when the cover is rendered outside Gallery/index.
  const photoCount = photos.length;
  const selectedCount = selectedProp ?? gallery?._selectedCount ?? 0;

  // Split the title across two lines with a "/" separator (e.g. "Spring /
  // Editorial"). If the title is one or two words, keep it on a single line.
  const splitTitle = useMemo(() => {
    const t = String(gallery?.name || "Gallery").trim();
    const words = t.split(/\s+/);
    if (words.length <= 2) return [t, null];
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
  }, [gallery?.name]);

  // Month + year stamp for the rotated caption. Always renders a valid stamp,
  // even on a freshly-seeded gallery without a created_at value.
  const monthYear = useMemo(() => {
    const raw = gallery?.created_at || gallery?.sent_at;
    const d = raw ? new Date(raw) : new Date();
    if (Number.isNaN(d.getTime())) return new Date().toLocaleString("en-US", { month: "long", year: "numeric" }).toUpperCase();
    return d.toLocaleString("en-US", { month: "long", year: "numeric" }).toUpperCase();
  }, [gallery?.created_at, gallery?.sent_at]);

  const studioName = gallery?.studio_name || gallery?.public_name || "";
  const customer = gallery?.customer_name || gallery?.client_name || "";
  const welcomeMsg = gallery?.welcome_message ||
    "A first look at our editorial — please favorite the frames you would like to take into final edits.";
  const subtitle = gallery?.subtitle || customer || studioName;

  const tickerItems = [
    "GALLERY",
    `${photoCount} FRAMES`,
    `${selectedCount} SELECTED`,
  ];

  const heroRef = useRef(null);

  return (
    <header
      ref={heroRef}
      className="relative w-full h-[100vh] min-h-[640px] overflow-hidden text-[color:var(--gallery-cover-ink,#FAF9F6)]"
      style={{ backgroundColor: isPage ? "rgb(var(--gallery-overlay-rgb, 10 10 9))" : "#0A0A09" }}
      data-testid="gallery-cover"
    >
      {/* Background photograph with parallax — interactive galleries wrap
          this in CoverReveal for a one-shot scale 1.05→1.0 reveal that
          composes cleanly with the scroll-coupled outer transform. */}
      {/* Image (geometry per layout) + tenant-driven vignette, scoped to the
          image box so page-style layouts keep a clean solid page around it. */}
      <div aria-hidden="true" className={`${IMG_BOX} overflow-hidden`}>
        {bg ? (
          <CoverReveal active={gallery?.type === "interactive"}>
            <motion.div
              className="absolute inset-0"
              style={{ y, scale, backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: `${gallery?.cover_focal_x ?? 50}% ${gallery?.cover_focal_y ?? 50}%` }}
            />
          </CoverReveal>
        ) : (
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#1A1816,#0A0A09)]" />
        )}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at center, rgba(var(--gallery-overlay-rgb, 0 0 0), calc(var(--gallery-overlay-opacity, 0.3) * 0.5)) 0%, rgba(var(--gallery-overlay-rgb, 0 0 0), calc(var(--gallery-overlay-opacity, 0.3) * 1.83)) 60%, rgba(var(--gallery-overlay-rgb, 0 0 0), calc(var(--gallery-overlay-opacity, 0.3) * 2.67)) 100%)` }} />
        <div className="absolute inset-0"
          style={{ background: `linear-gradient(to top, rgba(var(--gallery-overlay-rgb, 0 0 0), calc(var(--gallery-overlay-opacity, 0.3) * 2.83)) 0%, transparent 40%, rgba(var(--gallery-overlay-rgb, 0 0 0), calc(var(--gallery-overlay-opacity, 0.3) * 0.83)) 100%)` }} />
      </div>
      <FilmGrain opacity={0.06} tone="cool" />
      {L === "stripe" && (
        <div aria-hidden="true" className="absolute top-20 inset-x-8 lg:inset-x-16 h-px bg-[color:var(--gold-foil)]/60 z-10" />
      )}

      {/* TOP-LEFT — brand mark */}
      <div className="absolute top-6 left-6 lg:top-9 lg:left-12 z-20 flex items-center gap-3">
        {studioName.toLowerCase().includes("allure") ? (
          <span className="font-serif text-2xl text-[color:var(--gallery-cover-ink,#FAF9F6)] tracking-[0.2em]">ALLURE</span>
        ) : (
          <LumetierLogo size={32} inverted accent="var(--gold-base)" />
        )}
        <span className="ed-eyebrow text-[color:var(--gold-foil)] hidden sm:inline-block">
          {studioName || "STUDIO"}
        </span>
      </div>

      {/* TOP-RIGHT — ticker */}
      <div className="absolute top-6 right-6 lg:top-9 lg:right-12 z-20 hidden sm:block">
        <div className="backdrop-blur-sm bg-[#0A0A09]/40 border border-[color:var(--gold-foil)]/30 px-4 py-2">
          <div className="flex items-center gap-4">
            {tickerItems.map((it, i) => (
              <span key={i} className="ed-eyebrow text-[color:var(--gallery-cover-ink,#FAF9F6)]/90 inline-flex items-center gap-3">
                {it}
                {i < tickerItems.length - 1 ? <span className="text-[10px] text-[color:var(--gold-foil)]">·</span> : null}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* LEFT EDGE — rotated chapter caption */}
      <div className="hidden md:block absolute left-4 bottom-32 z-20">
        <RotatedCaption tone="gold">{`CHAPTER I${monthYear ? " · " + monthYear : ""}`}</RotatedCaption>
      </div>

      {/* Title card — placement per layout */}
      <div className={`relative z-10 h-full max-w-[1480px] mx-auto px-6 lg:px-12 flex ${CONTENT}`}>
        <div className={L === "journal" ? "w-full md:w-[34%] md:pr-2" : "max-w-3xl"}>
          <DisplayHeadline size="cover" tone="charcoal" className="text-[color:var(--gallery-cover-ink,#FAF9F6)] leading-[0.95]">
            {splitTitle[1] ? (
              <>
                {splitTitle[0]}
                <span className="text-[color:var(--gold-foil)]"> / </span>
                <em>{splitTitle[1]}</em>
              </>
            ) : (
              <em>{splitTitle[0]}</em>
            )}
          </DisplayHeadline>

          {subtitle ? (
            <div className="mt-6 inline-flex items-baseline gap-3">
              <span className="w-1 h-1 rounded-full bg-[color:var(--gold-base)]" aria-hidden="true" />
              <span className="font-serif italic text-2xl md:text-3xl text-[color:var(--gallery-cover-ink,#FAF9F6)]/90">{subtitle}</span>
            </div>
          ) : null}

          {/* G3.2 — gold hairline couture finish (interactive galleries only). */}
          {gallery?.type === "interactive" && (
            <motion.span
              aria-hidden="true"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{
                scaleX: { duration: 0.6, delay: 1.0, ease: [0.22, 1, 0.36, 1] },
                opacity: { duration: 0.2, delay: 1.0 },
              }}
              className="block w-[120px] h-px mt-6 origin-left"
              style={{ backgroundColor: "var(--gold-base, #C9A55C)" }}
            />
          )}

          <GoldRule width="3rem" className={`mt-7 ${centered ? "mx-auto" : ""}`} />

          {customer ? (
            <div className="mt-5">
              <ChapterEyebrow label={`For ${customer}`} tone="gold" />
            </div>
          ) : null}

          <p className={`font-serif italic text-lg md:text-xl mt-6 max-w-xl ${centered ? "mx-auto" : ""} text-[color:var(--gallery-cover-ink,#FAF9F6)]/82 leading-relaxed`}>
            {welcomeMsg}
          </p>
        </div>
      </div>

      {/* BOTTOM-CENTER — vertical scroll cue (hidden on layouts whose bottom
          band is occupied by the title page) */}
      {!["divider", "novel"].includes(L) && (
      <div className="absolute left-1/2 -translate-x-1/2 bottom-6 lg:bottom-10 z-20 flex flex-col items-center gap-3">
        <span className="ed-eyebrow text-[color:var(--gallery-cover-ink,#FAF9F6)]/75">SCROLL ↓</span>
        <motion.span
          aria-hidden="true"
          className="block w-px bg-[color:var(--gold-foil)] origin-top"
          style={{ height: 64 }}
          animate={{ scaleY: [0.2, 1, 0.2] }}
          transition={{ repeat: Infinity, duration: 1.9, ease: "easeInOut" }}
        />
      </div>
      )}
    </header>
  );
}
